<template>
  <div class="page-stack">
    <div class="sticky-toolbar">
      <a-card :bordered="false" class="sticky-toolbar-card">
        <a-space wrap align="center">
          <div class="toolbar-label">Дата:</div>
          <a-button @click="shiftDate(-7)"><LeftOutlined /></a-button>
          <a-date-picker v-model:value="dateValue" format="YYYY-MM-DD" :allowClear="false" inputReadOnly />
          <a-button @click="shiftDate(7)"><RightOutlined /></a-button>
          <a-button @click="load" :loading="loading">Обновить</a-button>
          <a-button type="primary" @click="printPage">Печать</a-button>
          <div class="toolbar-range">
            Неделя: {{ weekLabel }}
          </div>
        </a-space>
      </a-card>
    </div>

    <a-row :gutter="[12, 12]">
      <a-col :xs="12" :md="6">
        <a-card size="small">
          <a-statistic title="Завтрак" :value="weekTotals.BREAKFAST" />
        </a-card>
      </a-col>

      <a-col :xs="12" :md="6">
        <a-card size="small">
          <a-statistic title="Обед" :value="weekTotals.LUNCH" />
        </a-card>
      </a-col>

      <a-col :xs="12" :md="6">
        <a-card size="small">
          <a-statistic title="Полдник" :value="weekTotals.SNACK" />
        </a-card>
      </a-col>

      <a-col :xs="12" :md="6">
        <a-card size="small">
          <a-statistic title="Ужин" :value="weekTotals.DINNER" />
        </a-card>
      </a-col>
    </a-row>

    <a-card title="Отчет за неделю" :bordered="false">
      <a-collapse>
        <a-collapse-panel v-for="item in days" :key="item.date" :class="{ 'selected-day-panel': item.date === selectedDate }">
          <template #header>
            <div class="order-panel-header">
              <div class="order-panel-main">
                <strong class="order-panel-name">{{ item.headerTitle }}</strong>
              </div>

              <div class="order-panel-stats">
                <span class="order-stat"><strong>З:</strong> {{ item.meals.BREAKFAST }}</span>
                <span class="order-stat"><strong>О:</strong> {{ item.meals.LUNCH }}</span>
                <span class="order-stat"><strong>П:</strong> {{ item.meals.SNACK }}</span>
                <span class="order-stat"><strong>У:</strong> {{ item.meals.DINNER }}</span>
              </div>

              <div class="order-panel-status">
                <a-tag>{{ item.exists ? label(item.order.status || 'DRAFT') : 'Нет заказа' }}</a-tag>
              </div>
            </div>
          </template>

          <a-space direction="vertical" style="width: 100%" size="middle">
            <a-descriptions :column="1" bordered size="small">
              <a-descriptions-item label="Комментарий">
                {{ item.order.comment || '—' }}
              </a-descriptions-item>
            </a-descriptions>

            <a-row :gutter="[12, 12]">
              <a-col :xs="12" :md="6" v-for="meal in mealCards" :key="`${item.date}-${meal.meal}`">
                <a-card size="small" :title="meal.title">
                  <div><strong>Порций:</strong> {{ item.meals[meal.meal] }}</div>
                  <div style="margin-top: 8px">
                    <strong>Особые:</strong>
                    <div v-if="item.specials[meal.meal]?.length">
                      <a-tag
                        v-for="sp in item.specials[meal.meal]"
                        :key="`${item.date}-${meal.meal}-${sp.special_type_id || sp.id || sp.name}`"
                        style="margin-top: 4px"
                      >
                        {{ sp.name }}: {{ sp.qty }}
                      </a-tag>
                    </div>
                    <div v-else>—</div>
                  </div>
                </a-card>
              </a-col>
            </a-row>

            <a-tabs>
              <a-tab-pane v-for="meal in mealCards" :key="`${item.date}-${meal.meal}-children`" :tab="meal.title">
                <a-table
                  :dataSource="item.children[meal.meal] || []"
                  :columns="childColumns"
                  :pagination="false"
                  rowKey="child_id"
                  size="small"
                />
              </a-tab-pane>
            </a-tabs>
          </a-space>
        </a-collapse-panel>
      </a-collapse>
    </a-card>

    <section class="print-sheet">
      <header class="print-header">
        <h1>Отчет заказчика за неделю</h1>
        <div>{{ weekLabel }}</div>
      </header>

      <table class="print-table">
        <thead>
          <tr>
            <th>Дата</th>
            <th>Завтрак</th>
            <th>Обед</th>
            <th>Полдник</th>
            <th>Ужин</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in days" :key="`print-${item.date}`">
            <td class="print-date-cell">
              <div class="print-date">{{ item.headerTitle }}</div>
              <div class="print-status">{{ item.exists ? label(item.order.status || 'DRAFT') : 'Нет заказа' }}</div>
            </td>

            <td v-for="meal in mealCards" :key="`print-${item.date}-${meal.meal}`" class="print-meal-cell">
              <div class="print-meal-summary">{{ item.meals[meal.meal] }}</div>
              <div v-if="item.children[meal.meal]?.length" class="print-children">
                <div
                  v-for="child in item.children[meal.meal]"
                  :key="`print-${item.date}-${meal.meal}-${child.child_id}`"
                  class="print-child"
                >
                  {{ child.full_name }}
                  <span v-if="child.tags?.length" class="print-tag-icons">
                    {{ child.tags.map(tagToPrintIcon).filter(Boolean).join(' ') }}
                  </span>
                </div>
              </div>
            </td>
          </tr>

          <tr class="total-row">
            <td class="print-date-cell">
              <div class="print-date">Итого</div>
            </td>

            <td v-for="meal in mealCards" :key="`print-total-${meal.meal}`" class="print-meal-cell">
              <div class="print-meal-summary">{{ weekTotals[meal.meal] }}</div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script setup>
import { h, ref, computed, watch } from 'vue'
import dayjs from 'dayjs'
import { message } from 'ant-design-vue'
import { LeftOutlined, RightOutlined } from '@ant-design/icons-vue'
import { getOrderByDate } from '../../api/orders'
import ChildTagIcons from '../../components/ChildTagIcons.vue'
import { label } from '../../utils/labels'

const loading = ref(false)
const dateValue = ref(dayjs())
const days = ref([])

const mealCards = [
  { meal: 'BREAKFAST', title: 'Завтрак' },
  { meal: 'LUNCH', title: 'Обед' },
  { meal: 'SNACK', title: 'Полдник' },
  { meal: 'DINNER', title: 'Ужин' },
]

const childColumns = [
  { title: 'Ребёнок', dataIndex: 'full_name' },
  {
    title: 'Теги',
    dataIndex: 'tags',
    customRender: ({ text }) => h(ChildTagIcons, { tags: text }),
  },
  { title: 'Комментарий', dataIndex: 'child_comment' },
  { title: 'Примечание к заказу', dataIndex: 'order_note' },
]

const weekDates = computed(() => {
  const start = dayjs(dateValue.value).startOf('week')
  return Array.from({ length: 7 }, (_, index) => start.add(index, 'day'))
})

const weekLabel = computed(() => {
  const start = weekDates.value[0]
  const end = weekDates.value[6]
  return `${start.format('YYYY-MM-DD')} - ${end.format('YYYY-MM-DD')}`
})

const weekTotals = computed(() => {
  const totals = {
    BREAKFAST: 0,
    LUNCH: 0,
    SNACK: 0,
    DINNER: 0,
  }

  for (const item of days.value) {
    for (const meal of Object.keys(totals)) {
      totals[meal] += Number(item.meals[meal] || 0)
    }
  }

  return totals
})

const selectedDate = computed(() => dayjs(dateValue.value).format('YYYY-MM-DD'))

function shiftDate(daysShift) {
  dateValue.value = dayjs(dateValue.value).add(daysShift, 'day')
}

function tagToPrintIcon(tag) {
  const normalized = String(tag || '').trim().toLowerCase()

  if (['безглютеновая', 'без глютена', 'gluten free', 'gluten-free'].includes(normalized)) return '[БГ]'
  if (['безмолочная', 'без молока', 'dairy free', 'dairy-free'].includes(normalized)) return '[БМ]'
  if (['корректировка'].includes(normalized)) return '[К]'

  return ''
}

function normalizeOrderData(date, payload) {
  const data = payload?.data?.data || payload?.data || {}
  const order = data.order || {}

  return {
    date,
    exists: !!order.id,
    headerTitle: `${dayjs(date).format('YYYY-MM-DD, dd')}`,
    order: {
      id: order.id || null,
      status: order.status || null,
      comment: order.comment || '',
    },
    meals: {
      BREAKFAST: data.meals?.BREAKFAST || 0,
      LUNCH: data.meals?.LUNCH || 0,
      SNACK: data.meals?.SNACK || 0,
      DINNER: data.meals?.DINNER || 0,
    },
    specials: {
      BREAKFAST: data.specials?.BREAKFAST || [],
      LUNCH: data.specials?.LUNCH || [],
      SNACK: data.specials?.SNACK || [],
      DINNER: data.specials?.DINNER || [],
    },
    children: {
      BREAKFAST: data.children?.BREAKFAST || [],
      LUNCH: data.children?.LUNCH || [],
      SNACK: data.children?.SNACK || [],
      DINNER: data.children?.DINNER || [],
    },
  }
}

async function load() {
  loading.value = true
  try {
    const responses = await Promise.all(
      weekDates.value.map(async date => {
        const dateStr = date.format('YYYY-MM-DD')
        const res = await getOrderByDate(dateStr)
        return normalizeOrderData(dateStr, res)
      })
    )

    days.value = responses
  } catch (e) {
    message.error(e?.response?.data?.error?.message || 'Не удалось загрузить недельный отчет')
  } finally {
    loading.value = false
  }
}

function printPage() {
  window.print()
}

watch(dateValue, () => {
  load()
})

load()
</script>

<style scoped>
.page-stack {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.sticky-toolbar {
  position: sticky;
  top: 16px;
  z-index: 20;
}

.sticky-toolbar-card :deep(.ant-card-body) {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
}

.toolbar-label {
  font-weight: 600;
}

.toolbar-range {
  color: #666;
}

.print-sheet {
  display: none;
}

.order-panel-header {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.order-panel-main {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 0 0 220px;
  width: 220px;
  min-width: 0;
}

.order-panel-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-panel-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(58px, max-content));
  gap: 12px;
  align-items: center;
  justify-content: start;
  flex: 1 1 auto;
}

.order-stat {
  white-space: nowrap;
  text-align: left;
}

:deep(.selected-day-panel .ant-collapse-header) {
  background: #eaf4ff !important;
}

.print-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 12px;
}

.print-header h1 {
  margin: 0;
  font-size: 24px;
}

.print-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  background: #fff;
}

.print-table th,
.print-table td {
  border: 1px solid #222;
  padding: 8px;
  vertical-align: top;
}

.print-table th {
  text-align: center;
  font-weight: 700;
}

.print-date-cell {
  width: 16%;
}

.print-date {
  font-weight: 700;
}

.print-status {
  margin-top: 4px;
  font-size: 12px;
  color: #666;
}

.print-meal-cell {
  width: 21%;
}

.print-meal-summary {
  font-weight: 700;
  margin-bottom: 6px;
}

.print-children {
  font-size: 10px;
}

.print-child {
  white-space: nowrap;
}

.print-tag-icons {
  margin-left: 4px;
  font-size: 9px;
}

@media (max-width: 767px) {
  .sticky-toolbar {
    top: 8px;
  }

  .order-panel-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .order-panel-main {
    width: 100%;
    flex: 1 1 auto;
    flex-wrap: wrap;
  }

  .order-panel-stats {
    grid-template-columns: repeat(2, max-content);
    gap: 8px 14px;
    justify-content: start;
  }
}

@media print {
  .sticky-toolbar,
  :deep(.ant-row),
  :deep(.ant-card) {
    display: none !important;
  }

  .print-sheet {
    display: block;
  }

  @page {
    margin: 6mm;
  }
}
</style>
